// SCHEDULE_MODEL_PHASE10_20260911
import { useMemo, useState } from 'react';
import {
  getScheduleItemChronologyValue,
  getScheduleItemOccupancy,
  getScheduleItemSource,
  getSharedSchedulePoolItemKey
} from '../../../utils/studentAssignmentSchedule';

function sortAssignments(assignments) {
  return [...(assignments || [])].sort(
    (a, b) =>
      (Number(a.instance_number) || 0) -
        (Number(b.instance_number) || 0) ||
      String(a.id).localeCompare(String(b.id))
  );
}

export default function SharedScheduleChoiceModal({
  assignments,
  context,
  requiredCount,
  pendingByAssignment,
  setPendingByAssignment,
  getSelections,
  getMembership,
  assignmentUsesGroups,
  assignmentLabel,
  saving,
  error,
  confirming,
  setConfirming,
  onConfirm,
  onCancel,
  onOpenReading,
  groupNameForAssignment
}) {
  const [localError, setLocalError] = useState('');

  const orderedAssignments = useMemo(
    () => sortAssignments(assignments),
    [assignments]
  );

  const itemById = useMemo(() => {
    const map = new Map();
    (context?.scheduleItems || [])
      .filter(item => item.configuration_mode === 'choice')
      .forEach(item => {
        map.set(String(item.id), item);
      });
    return map;
  }, [context?.scheduleItems]);

  function linkedItemsFor(assignmentId) {
    return (context?.scheduleItems || [])
      .filter(
        item =>
          String(item.course_assignment_id) ===
            String(assignmentId) &&
          item.configuration_mode === 'choice'
      )
      .sort(
        (a, b) => {
          const aTime =
            getScheduleItemChronologyValue(
              a,
              context
            );

          const bTime =
            getScheduleItemChronologyValue(
              b,
              context
            );


          if (
            aTime !== null &&
            bTime !== null &&
            aTime !== bTime
          ) {
            return aTime - bTime;
          }


          if (
            aTime !== null &&
            bTime === null
          ) {
            return -1;
          }


          if (
            aTime === null &&
            bTime !== null
          ) {
            return 1;
          }


          return (
            (Number(a.display_order) || 0) -
              (Number(b.display_order) || 0) ||
            String(a.id).localeCompare(
              String(b.id)
            )
          );
        }
      );
  }

  function pendingIdsFor(assignmentId) {
    return pendingByAssignment?.[assignmentId] || [];
  }

  function existingIdsFor(assignmentId) {
    return (getSelections(assignmentId) || []).map(
      selection => selection.course_assignment_schedule_item_id
    );
  }

  function finalIdsFor(assignmentId) {
    return [
      ...existingIdsFor(assignmentId),
      ...pendingIdsFor(assignmentId)
    ];
  }

  function pendingCountForPool(poolKey) {
    let count = 0;

    Object.values(pendingByAssignment || {}).forEach(ids => {
      (ids || []).forEach(id => {
        const item = itemById.get(String(id));
        if (!item) return;

        if (
          String(getSharedSchedulePoolItemKey(item, context)) ===
          String(poolKey)
        ) {
          count += 1;
        }
      });
    });

    return count;
  }

  function togglePending(assignment, item) {
    if (saving) return;

    const assignmentId = assignment.id;
    const existingIds = new Set(
      existingIdsFor(assignmentId).map(String)
    );

    if (existingIds.has(String(item.id))) return;

    const current = pendingIdsFor(assignmentId);
    const exists = current.some(
      id => String(id) === String(item.id)
    );

    if (exists) {
      setPendingByAssignment(prev => ({
        ...prev,
        [assignmentId]: (prev?.[assignmentId] || []).filter(
          id => String(id) !== String(item.id)
        )
      }));
      setLocalError('');
      return;
    }

    const existingCount = existingIds.size;
    const remaining = Math.max(0, requiredCount - existingCount);

    if (remaining <= 0) return;

    const poolKey = getSharedSchedulePoolItemKey(item, context);
    const occupancy = getScheduleItemOccupancy(context, item.id);
    const capacity =
      item.capacity === null || item.capacity === undefined
        ? null
        : Number(item.capacity);
    const pendingForPool = pendingCountForPool(poolKey);

    if (
      capacity !== null &&
      occupancy + pendingForPool >= capacity
    ) {
      setLocalError('That option is already at capacity.');
      return;
    }

    setPendingByAssignment(prev => {
      const currentIds = prev?.[assignmentId] || [];

      if (remaining === 1) {
        return {
          ...prev,
          [assignmentId]: [item.id]
        };
      }

      if (currentIds.length >= remaining) {
        return prev;
      }

      return {
        ...prev,
        [assignmentId]: [...currentIds, item.id]
      };
    });

    setLocalError('');
  }

  function completenessError() {
    for (const assignment of orderedAssignments) {
      const finalCount = finalIdsFor(assignment.id).length;

      if (finalCount !== requiredCount) {
        const label = assignmentLabel(assignment);
        return `${label} needs ${requiredCount} selection${requiredCount === 1 ? '' : 's'}.`;
      }
    }

    return '';
  }

  function chronologyError() {
    let previousMax = null;
    let previousLabel = '';

    for (const assignment of orderedAssignments) {
      const label = assignmentLabel(assignment);
      const values = finalIdsFor(assignment.id)
        .map(id => itemById.get(String(id)))
        .filter(Boolean)
        .map(item => getScheduleItemChronologyValue(item, context));

      if (
        values.length !== requiredCount ||
        values.some(value => value === null)
      ) {
        return `One or more selections for ${label} do not have a usable date, so their order cannot be checked.`;
      }

      const currentMin = Math.min(...values);
      const currentMax = Math.max(...values);

      if (previousMax !== null && currentMin <= previousMax) {
        return (
          `Your selection for ${label} is earlier than for ${previousLabel}. ` +
          `Choose a later option for ${label}, or change your selection for ${previousLabel}.`
        );
      }

      previousMax = currentMax;
      previousLabel = label;
    }

    return '';
  }

  const countError = completenessError();
  const orderError = countError ? '' : chronologyError();
  const validationError = countError || orderError;
  const displayedError = error || localError || orderError;

  function continueToConfirmation() {
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setLocalError('');
    setConfirming(true);
  }

  const usesGroups = orderedAssignments.some(assignment =>
    assignmentUsesGroups(assignment)
  );
  const firstMembership = orderedAssignments
    .map(assignment => getMembership(assignment.id))
    .find(Boolean);

  return (
    <div style={styles.overlay} onMouseDown={onCancel}>
      <div
        style={styles.panel}
        onMouseDown={event => event.stopPropagation()}
      >
        <div style={styles.header}>
          <div>
            <div style={styles.title}>
              {confirming
                ? 'Confirm choices'
                : 'Choose for all assignment instances'}
            </div>
            <div style={styles.subtitle}>
              Choose {requiredCount} item{requiredCount === 1 ? '' : 's'} for each assignment. Capacities are shared across the whole Schedule.
            </div>
          </div>

          <button
            type="button"
            style={styles.close}
            disabled={saving}
            onClick={onCancel}
            aria-label="Close choices"
          >
            ×
          </button>
        </div>

        {displayedError && (
          <div style={styles.error}>{displayedError}</div>
        )}

        {!confirming ? (
          <>
            <div style={styles.instructions}>
              Make all of your selections before confirming. Each later assignment must use an item later than every item selected for the previous assignment.
            </div>

            <div style={styles.assignments}>
              {orderedAssignments.map(assignment => {
                const linkedItems = linkedItemsFor(assignment.id);
                const existingSelections = getSelections(assignment.id) || [];
                const existingIds = new Set(
                  existingSelections.map(selection =>
                    String(selection.course_assignment_schedule_item_id)
                  )
                );
                const pendingIds = new Set(
                  pendingIdsFor(assignment.id).map(String)
                );
                const finalCount = existingIds.size + pendingIds.size;

                return (
                  <section key={assignment.id} style={styles.assignmentBlock}>
                    <div style={styles.assignmentHeader}>
                      <div style={styles.assignmentTitle}>
                        {assignmentLabel(assignment)}
                      </div>
                      <div style={styles.assignmentCount}>
                        {finalCount}/{requiredCount} selected
                      </div>
                    </div>

                    <div style={styles.options}>
                      {linkedItems.map(item => {
                        const source = getScheduleItemSource(item, context);
                        const alreadySelected = existingIds.has(String(item.id));
                        const pending = pendingIds.has(String(item.id));
                        const poolKey = getSharedSchedulePoolItemKey(item, context);
                        const occupancy = getScheduleItemOccupancy(context, item.id);
                        const capacity =
                          item.capacity === null || item.capacity === undefined
                            ? null
                            : Number(item.capacity);
                        const pendingForPool = pendingCountForPool(poolKey);
                        const full =
                          capacity !== null &&
                          occupancy + pendingForPool >= capacity;
                        const assignmentAlreadyComplete =
                          existingIds.size >= requiredCount;
                        const disabled =
                          saving ||
                          alreadySelected ||
                          (assignmentAlreadyComplete && !pending) ||
                          (full && !pending);

                        return (
                          <label
                            key={item.id}
                            style={{
                              ...styles.option,
                              ...((alreadySelected || pending)
                                ? styles.optionSelected
                                : {}),
                              ...(disabled && !alreadySelected && !pending
                                ? styles.optionDisabled
                                : {})
                            }}
                          >
                            <input
                              type={requiredCount === 1 ? 'radio' : 'checkbox'}
                              name={`shared-choice-${assignment.id}`}
                              checked={alreadySelected || pending}
                              disabled={disabled}
                              onChange={() => togglePending(assignment, item)}
                            />

                            <div style={styles.optionMain}>
                              <div style={styles.optionHeading}>
                                <span style={styles.typePill}>{source.typeLabel}</span>
                                {source.type === 'reading' ? (
                                  <button
                                    type="button"
                                    style={styles.readingLink}
                                    onClick={event => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      onOpenReading(source.reading);
                                    }}
                                  >
                                    {source.title}
                                  </button>
                                ) : (
                                  <strong>{source.title}</strong>
                                )}
                              </div>

                              {source.meta && (
                                <div style={styles.meta}>{source.meta}</div>
                              )}

                              {source.anchorDate && (
                                <div style={styles.date}>
                                  {source.anchorDate}
                                  {source.eventTime
                                    ? ` · ${String(source.eventTime).slice(0, 5)}`
                                    : ''}
                                </div>
                              )}
                            </div>

                            <div style={styles.optionSide}>
                              {alreadySelected && (
                                <span style={styles.finalPill}>Final</span>
                              )}
                              {capacity !== null && (
                                <span style={styles.capacity}>
                                  {occupancy}/{capacity} used
                                </span>
                              )}
                              {!alreadySelected && full && !pending && (
                                <span style={styles.full}>Full</span>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>

            <div style={styles.actions}>
              <button
                type="button"
                style={styles.secondaryBtn}
                disabled={saving}
                onClick={onCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                style={styles.primaryBtn}
                disabled={saving || !!countError}
                onClick={continueToConfirmation}
              >
                Continue
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={styles.confirmList}>
              {orderedAssignments.map(assignment => {
                const finalIds = new Set(
                  finalIdsFor(assignment.id).map(String)
                );
                const items = linkedItemsFor(assignment.id).filter(item =>
                  finalIds.has(String(item.id))
                );

                return (
                  <div key={assignment.id} style={styles.confirmAssignment}>
                    <div style={styles.confirmAssignmentTitle}>
                      {assignmentLabel(assignment)}
                    </div>
                    {items.map(item => {
                      const source = getScheduleItemSource(item, context);
                      return (
                        <div key={item.id} style={styles.confirmRow}>
                          <span style={styles.typePill}>{source.typeLabel}</span>
                          <strong>{source.title}</strong>
                          <span style={styles.confirmDate}>
                            {source.anchorDate || ''}
                            {source.eventTime
                              ? ` ${String(source.eventTime).slice(0, 5)}`
                              : ''}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            <div style={styles.warning}>
              {usesGroups && firstMembership ? (
                <>
                  You are confirming these choices for <strong>{
                    groupNameForAssignment
                      ? groupNameForAssignment(
                          firstMembership.course_assignment_id,
                          firstMembership.group_number
                        )
                      : `Group ${firstMembership.group_number}`
                  }</strong>. They apply to every member of the group and cannot be changed by students after confirmation.
                </>
              ) : (
                <>
                  These choices are final and cannot be changed by students after confirmation.
                </>
              )}
            </div>

            <div style={styles.actions}>
              <button
                type="button"
                style={styles.secondaryBtn}
                disabled={saving}
                onClick={() => setConfirming(false)}
              >
                Back
              </button>
              <button
                type="button"
                style={styles.primaryBtn}
                disabled={saving}
                onClick={onConfirm}
              >
                {saving ? 'Confirming...' : 'Confirm all choices'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.58)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 450, padding: '20px'
  },
  panel: {
    width: '100%', maxWidth: '920px', maxHeight: '92vh', overflowY: 'auto',
    background: 'white', borderRadius: '10px', boxShadow: '0 18px 50px rgba(0,0,0,0.25)'
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px',
    padding: '16px 18px', borderBottom: '1px solid #e5eaee'
  },
  title: { fontSize: '18px', fontWeight: '800', color: '#243b53' },
  subtitle: { fontSize: '12px', color: '#627d98', marginTop: '3px', lineHeight: 1.45 },
  close: { border: 'none', background: 'none', fontSize: '26px', cursor: 'pointer', color: '#627d98' },
  error: {
    margin: '12px 18px 0', background: '#faeaea', border: '1px solid #e8bcbc',
    borderRadius: '7px', padding: '9px 10px', color: '#a12a2a', fontSize: '12px'
  },
  instructions: { padding: '12px 18px 4px', fontSize: '13px', color: '#486581', lineHeight: 1.45 },
  assignments: { padding: '8px 18px 14px', display: 'flex', flexDirection: 'column', gap: '13px' },
  assignmentBlock: { border: '1px solid #d9e2ec', borderRadius: '9px', overflow: 'hidden' },
  assignmentHeader: {
    display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center',
    padding: '9px 11px', background: '#f7f9fb', borderBottom: '1px solid #e5eaee'
  },
  assignmentTitle: { fontSize: '14px', fontWeight: '800', color: '#334e68' },
  assignmentCount: { fontSize: '11px', fontWeight: '700', color: '#627d98' },
  options: { padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' },
  option: {
    display: 'flex', alignItems: 'flex-start', gap: '9px', padding: '9px',
    border: '1px solid #d9e2ec', borderRadius: '7px', background: '#fbfcfd', cursor: 'pointer'
  },
  optionSelected: { border: '1px solid #9fc8ad', background: '#f3faf6' },
  optionDisabled: { opacity: 0.55, cursor: 'default' },
  optionMain: { minWidth: 0, flex: 1 },
  optionHeading: { display: 'flex', gap: '7px', alignItems: 'center', flexWrap: 'wrap', color: '#334e68' },
  optionSide: { display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-end', flexShrink: 0 },
  typePill: {
    padding: '2px 6px', borderRadius: '999px', background: '#e7f1f7', color: '#215b7d',
    fontSize: '9px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.05em'
  },
  readingLink: {
    border: 'none', background: 'none', color: '#2f6f9f', fontFamily: 'inherit', fontWeight: '700',
    cursor: 'pointer', textDecoration: 'underline', padding: 0, textAlign: 'left'
  },
  meta: { color: '#627d98', fontSize: '11px', marginTop: '2px' },
  date: { color: '#829ab1', fontSize: '11px', marginTop: '3px' },
  finalPill: {
    borderRadius: '999px', padding: '3px 7px', background: '#e7f5ed', color: '#216e45',
    fontSize: '10px', fontWeight: '800'
  },
  capacity: { fontSize: '10px', color: '#627d98' },
  full: { fontSize: '10px', color: '#a12a2a', fontWeight: '800' },
  actions: {
    display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '12px 18px 16px',
    borderTop: '1px solid #e5eaee'
  },
  primaryBtn: {
    border: 'none', borderRadius: '7px', padding: '8px 13px', background: '#215b7d',
    color: 'white', fontWeight: '800', cursor: 'pointer', fontSize: '13px'
  },
  secondaryBtn: {
    border: '1px solid #b7c7d3', borderRadius: '7px', padding: '8px 13px', background: 'white',
    color: '#334e68', fontWeight: '700', cursor: 'pointer', fontSize: '13px'
  },
  confirmList: { padding: '14px 18px 6px', display: 'flex', flexDirection: 'column', gap: '12px' },
  confirmAssignment: { border: '1px solid #e5eaee', borderRadius: '8px', overflow: 'hidden' },
  confirmAssignmentTitle: { padding: '8px 10px', background: '#f7f9fb', fontWeight: '800', color: '#334e68' },
  confirmRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderTop: '1px solid #eef2f5' },
  confirmDate: { marginLeft: 'auto', fontSize: '11px', color: '#829ab1' },
  warning: {
    margin: '10px 18px 14px', padding: '12px', background: '#fff7e6', border: '1px solid #f1d39a',
    borderRadius: '8px', color: '#7a5310', fontSize: '13px', lineHeight: 1.45
  }
};
